import {
  getCiProjectSpendShift,
  getDmsmSpendPartDetails,
  getSupplierLocationInfo
} from '../shared/APIHelper';
import * as XLSX from 'xlsx-js-style';
import type { SupplierSpendShiftResponse } from './SupplierSpendShift.types';

type ColumnDefinition = {
  header: string;
  key: string;
  width: number;
};

type WorksheetRow = Record<string, unknown>;

const sanitizeForExcel = (value: unknown) => {
  if (typeof value === 'string' && /^[=+\-@]/.test(value)) {
    return `'${value}`;
  }
  return value;
};

const buildWorksheet = (
  columns: ColumnDefinition[],
  rows: WorksheetRow[]
): XLSX.WorkSheet => {
  const sheetData = [
    columns.map((column) => column.header),
    ...rows.map((row) =>
      columns.map((column) => sanitizeForExcel(row[column.key] ?? ''))
    )
  ];
  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
  worksheet['!cols'] = columns.map((column) => ({ wch: column.width }));
  return worksheet;
};

const applyColumnNumberFormat = (
  worksheet: XLSX.WorkSheet,
  columnIndex: number,
  dataRowCount: number,
  numberFormat: string
) => {
  for (let rowIndex = 1; rowIndex <= dataRowCount; rowIndex += 1) {
    const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
    const cell = worksheet[cellAddress];
    if (cell) {
      cell.s = { ...(cell.s ?? {}), numFmt: numberFormat };
    }
  }
};

const applyColumnNumberFormats = (
  worksheet: XLSX.WorkSheet,
  columns: ColumnDefinition[],
  dataRowCount: number,
  formatsByKey: Record<string, string>
) => {
  columns.forEach((column, columnIndex) => {
    const numberFormat = formatsByKey[column.key];
    if (!numberFormat) {
      return;
    }
    applyColumnNumberFormat(worksheet, columnIndex, dataRowCount, numberFormat);
  });
};

const applyRowStyle = (
  worksheet: XLSX.WorkSheet,
  rowIndex: number,
  totalColumns: number,
  style: Record<string, unknown>
) => {
  for (let columnIndex = 0; columnIndex < totalColumns; columnIndex += 1) {
    const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
    const cell = worksheet[cellAddress];
    if (cell) {
      cell.s = { ...(cell.s ?? {}), ...style };
    }
  }
};

const pdpProgramColumns: ColumnDefinition[] = [
  {
    header: 'Start of Production Fiscal Year',
    key: 'startOfProductionFiscalYear',
    width: 20
  },
  { header: 'CS Program ID', key: 'csProgramId', width: 15 },
  { header: 'Unit Code', key: 'unitCode', width: 12 },
  { header: 'Machine Type', key: 'machineType', width: 15 },
  { header: 'Machine Number', key: 'machineNumber', width: 18 },
  { header: 'Machine Volume', key: 'machineVolume', width: 18 },
  { header: 'Part Number', key: 'partNumber', width: 15 },
  { header: 'Part Description', key: 'partDescription', width: 35 },
  { header: 'Material Group Code', key: 'materialGroupCode', width: 20 },
  { header: 'Supplier Number', key: 'supplierNumber', width: 18 },
  { header: 'Supplier Name', key: 'supplierName', width: 35 },
  { header: 'Part Quantity', key: 'partQuantity', width: 12 },
  { header: 'Extended Pieces', key: 'extendedPieces', width: 18 },
  { header: 'Piece Price (USD)', key: 'piecePrice', width: 12 },
  { header: 'Extended Cost', key: 'extendedCost', width: 15 },
  { header: 'DMSM Strategy Number', key: 'dmsmStrategyNumber', width: 25 }
];

const r12ForecastedColumns: ColumnDefinition[] = [
  { header: 'Part Number', key: 'partNumber', width: 20 },
  { header: 'Part Description', key: 'partDescription', width: 30 },
  { header: 'Unit Code', key: 'unitCode', width: 12 },
  { header: 'Supplier Number', key: 'supplierNumber', width: 18 },
  { header: 'Supplier Name', key: 'supplierName', width: 40 },
  { header: 'Quantity', key: 'quantity', width: 15 },
  { header: 'Piece Price (USD)', key: 'piecePrice', width: 15 },
  { header: 'Extended Cost', key: 'extendedCost', width: 15 }
];

const spendShiftColumns: ColumnDefinition[] = [
  { header: '', key: 'name', width: 60 },
  { header: 'SOP / Impl Date', key: 'sopImplDate', width: 18 },
  { header: 'Unit', key: 'unit', width: 10 },
  { header: 'R12 Invoiced', key: 'r12Invoiced', width: 18 },
  { header: 'R12 Forecasted', key: 'r12Forecasted', width: 18 }
];

const ciProjectColumns: ColumnDefinition[] = [
  { header: 'Fiscal Year', key: 'fiscalYear', width: 12 },
  { header: 'CI Project Number', key: 'ciProjectId', width: 20 },
  { header: 'Project Status', key: 'projectStatus', width: 20 },
  { header: 'Unit Code', key: 'unitCode', width: 12 },
  { header: 'Part Number', key: 'partNumber', width: 15 },
  { header: 'Current Supplier Number', key: 'supplierNumber', width: 18 },
  { header: 'Current Supplier Name', key: 'supplierName', width: 35 },
  { header: 'Current Piece Price (USD)', key: 'piecePrice', width: 15 },
  { header: 'New Supplier Number', key: 'newSupplierNumber', width: 18 },
  { header: 'New Supplier Name', key: 'newSupplierName', width: 35 },
  { header: 'New Piece Price (USD)', key: 'newPiecePrice', width: 15 },
  { header: '52 Week Forecast', key: 'forecast52Wk', width: 20 },
  { header: 'Extended Cost', key: 'extendedCost', width: 20 }
];

const CURRENCY_NUM_FMT = '_($* #,##0.00_);_($* -#,##0.00_);_($* "-"??_);_(@_)';

export async function exportSupplierSpendShiftPartDetails(
  suppliersAndUnitCodes: Parameters<typeof getDmsmSpendPartDetails>[0],
  filenameAddition: string,
  supplierSpendData: SupplierSpendShiftResponse,
  displayMode: 'spend' | 'pieces' = 'spend'
) {
  const toNumberOrNull = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const partDetails =
    (await getDmsmSpendPartDetails(suppliersAndUnitCodes)) ?? {};
  const ciProjectResponse = await getCiProjectSpendShift(
    suppliersAndUnitCodes,
    {
      pieces: displayMode === 'pieces'
    }
  ).catch(() => undefined);

  const dmsmParts = (partDetails.pdpParts ?? [])
    .filter((part): part is NonNullable<typeof part> => Boolean(part))
    .sort((a, b) => {
      // 1st Sort - Start of Production Fiscal Year (ascending)
      const aSopFiscalYear = a?.startOfProductionFiscalYear ?? 0;
      const bSopFiscalYear = b?.startOfProductionFiscalYear ?? 0;
      if (aSopFiscalYear !== bSopFiscalYear) {
        return aSopFiscalYear - bSopFiscalYear;
      }
      // 2nd Sort - CS Program ID (ascending)
      const aCsProgramId = a?.csProgramId ?? '';
      const bCsProgramId = b?.csProgramId ?? '';
      if (aCsProgramId !== bCsProgramId) {
        return aCsProgramId.localeCompare(bCsProgramId);
      }
      // 3rd Sort - Machine Type (ascending)
      const aMachineType = a?.machineType ?? '';
      const bMachineType = b?.machineType ?? '';
      if (aMachineType !== bMachineType) {
        return aMachineType.localeCompare(bMachineType);
      }
      // 4th Sort - Machine Number (ascending)
      const aMachineNumber = a?.machineNumber ?? '';
      const bMachineNumber = b?.machineNumber ?? '';
      if (aMachineNumber !== bMachineNumber) {
        return aMachineNumber.localeCompare(bMachineNumber);
      }
      // 5th Sort - Part Number (ascending)
      return (a?.partNumber ?? '').localeCompare(b?.partNumber ?? '');
    });

  const wb = XLSX.utils.book_new();

  const ciSupplierNumbers = (ciProjectResponse?.projects ?? []).flatMap(
    (project) =>
      (project.parts ?? []).flatMap((part) => [
        part.currentSupplierNumber,
        part.newSupplierNumber
      ])
  );
  const uniqueSupplierNumbers = [
    ...new Set(
      [
        ...dmsmParts.map((part) => part.supplierNumber),
        ...ciSupplierNumbers
      ].filter((value): value is string => Boolean(value))
    )
  ];
  const supplierNamesByNumbers =
    uniqueSupplierNumbers.length > 0
      ? (await getSupplierLocationInfo(uniqueSupplierNumbers)).nameMap
      : new Map();

  // ---- PDP Program Tab ----
  const pdpRows = dmsmParts.map((part) => ({
    ...part,
    supplierName:
      (part.supplierNumber
        ? supplierNamesByNumbers.get(part.supplierNumber)
        : undefined) ?? '',
    extendedPieces:
      part?.machineType === 'current'
        ? -((part?.machineVolume ?? 0) * (part?.partQuantity ?? 0))
        : (part?.machineVolume ?? 0) * (part?.partQuantity ?? 0),
    extendedCost:
      part?.machineType === 'current'
        ? -(part?.extendedCost ?? 0)
        : (part?.extendedCost ?? 0)
  }));
  const wsPdp = buildWorksheet(pdpProgramColumns, pdpRows);
  applyColumnNumberFormats(wsPdp, pdpProgramColumns, pdpRows.length, {
    machineVolume: '#,##0',
    partQuantity: '0',
    extendedPieces: '#,##0',
    piecePrice: CURRENCY_NUM_FMT,
    extendedCost: CURRENCY_NUM_FMT
  });
  XLSX.utils.book_append_sheet(wb, wsPdp, 'PDP Program');

  // ---- R12 Forecasted Tab ----
  const r12ForecastData = (partDetails.r12Forecasted ?? []).filter(
    (part): part is NonNullable<typeof part> => Boolean(part)
  );

  const wsR12 = buildWorksheet(r12ForecastedColumns, r12ForecastData);
  applyColumnNumberFormats(wsR12, r12ForecastedColumns, r12ForecastData.length, {
    quantity: '0',
    piecePrice: CURRENCY_NUM_FMT,
    extendedCost: CURRENCY_NUM_FMT
  });
  XLSX.utils.book_append_sheet(wb, wsR12, 'R12 Forecasted');

  // ---- Spend Shift Tab (PDP Programs + CI Projects combined) ----
  const fyLabels = supplierSpendData.fiscalYears.map((fy) => `FY${fy}`);
  const numFmt = displayMode === 'pieces' ? '#,##0' : CURRENCY_NUM_FMT;

  const spendShiftAllColumns: ColumnDefinition[] = [
    ...spendShiftColumns,
    ...fyLabels.map((label) => ({ header: label, key: label, width: 18 }))
  ];

  const sectionRowStyle = {
    font: { bold: true },
    fill: {
      patternType: 'solid',
      fgColor: { rgb: 'F5F5F5' },
      bgColor: { rgb: 'F5F5F5' }
    }
  };

  const spendShiftRows: WorksheetRow[] = [];
  const sectionRowIndexes: number[] = [];

  const addSectionRow = (
    name: string,
    r12Invoiced: number,
    r12Forecasted: number,
    fyAmounts: number[]
  ) => {
    spendShiftRows.push({
      name,
      r12Invoiced,
      r12Forecasted,
      ...Object.fromEntries(fyLabels.map((lbl, i) => [lbl, fyAmounts[i] ?? 0]))
    });
    sectionRowIndexes.push(spendShiftRows.length);
  };

  // Current Spend
  addSectionRow(
    'Current Spend',
    supplierSpendData.currentSpends.r12InvoiceSpend.amount,
    supplierSpendData.currentSpends.r12ForecastSpend.amount,
    supplierSpendData.currentSpends.fiscalYears.map((fy) => fy.amount)
  );

  // PDP Programs (from SEP)
  addSectionRow(
    'PDP Programs (from SEP)',
    supplierSpendData.totalPDPSpends.r12InvoiceSpend.amount,
    supplierSpendData.totalPDPSpends.r12ForecastSpend.amount,
    supplierSpendData.totalPDPSpends.fiscalYears.map((fy) => fy.amount)
  );
  for (const program of supplierSpendData.pdpPrograms) {
    spendShiftRows.push({
      name: `#${program.csProgramId} ${program.csProgramName ?? ''}`.trim(),
      sopImplDate: program.sop ?? '',
      unit: program.unitCode,
      r12Invoiced: program.r12InvoiceSpend.amount ?? null,
      r12Forecasted: program.r12ForecastSpend.amount ?? null,
      ...Object.fromEntries(
        fyLabels.map((lbl, i) => [lbl, program.fiscalYears[i]?.amount ?? null])
      )
    });
  }

  // CI Projects (from Forecast Projects)
  if (supplierSpendData.totalCISpends) {
    addSectionRow(
      'CI Projects (from Forecast Projects)',
      supplierSpendData.totalCISpends.r12InvoiceSpend.amount,
      supplierSpendData.totalCISpends.r12ForecastSpend.amount,
      supplierSpendData.totalCISpends.fiscalYears.map((fy) => fy.amount)
    );
  }
  for (const ci of supplierSpendData.ciProjects ?? []) {
    spendShiftRows.push({
      name: `${ci.projectId} ${ci.projectTitle}`.trim(),
      sopImplDate: ci.implDate ?? '',
      unit: ci.unitCode,
      r12Invoiced: null,
      r12Forecasted: null,
      ...Object.fromEntries(
        fyLabels.map((lbl, i) => [lbl, ci.fiscalYears[i]?.amount ?? null])
      )
    });
  }

  // Total Spend
  addSectionRow(
    'Total Spend',
    supplierSpendData.totalSpends.r12InvoiceSpend.amount,
    supplierSpendData.totalSpends.r12ForecastSpend.amount,
    supplierSpendData.totalSpends.fiscalYears.map((fy) => fy.amount)
  );

  const wsShift = buildWorksheet(spendShiftAllColumns, spendShiftRows);
  applyColumnNumberFormats(wsShift, spendShiftAllColumns, spendShiftRows.length, {
    r12Invoiced: numFmt,
    r12Forecasted: numFmt,
    ...Object.fromEntries(fyLabels.map((label) => [label, numFmt]))
  });
  sectionRowIndexes.forEach((rowIndex) => {
    applyRowStyle(wsShift, rowIndex, spendShiftAllColumns.length, sectionRowStyle);
  });
  XLSX.utils.book_append_sheet(wb, wsShift, 'Spend Shift');

  // ---- CI Projects Tab ----
  const selectedSupplierNumbers = new Set(
    suppliersAndUnitCodes.supplierNumbers
  );

  const ciProjectRows = (ciProjectResponse?.projects ?? []).flatMap(
    (project) => {
      const parts = project.parts ?? [];
      if (parts.length === 0) {
        return [
          {
            fiscalYear: project.fiscalYear ?? '',
            ciProjectId: project.projectId ?? '',
            projectStatus: project.projectStatus ?? '',
            unitCode: project.unitCode ?? '',
            partNumber: '',
            supplierNumber: '',
            supplierName: '',
            piecePrice: null,
            newSupplierNumber: '',
            newSupplierName: '',
            newPiecePrice: null,
            forecast52Wk: null,
            extendedCost: null
          }
        ];
      }

      return parts.map((part) => {
        const currentSupplierNumber = part.currentSupplierNumber ?? '';
        const newSupplierNumber = part.newSupplierNumber ?? '';
        const supplierIsCurrentSupplier =
          Boolean(currentSupplierNumber) &&
          selectedSupplierNumbers.has(currentSupplierNumber);
        const supplierIsNewSupplier =
          Boolean(newSupplierNumber) &&
          selectedSupplierNumbers.has(newSupplierNumber);
        const supplierIsBoth =
          supplierIsCurrentSupplier && supplierIsNewSupplier;

        const showCurrentSupplier = !supplierIsNewSupplier || supplierIsBoth;
        const showNewSupplier = !supplierIsCurrentSupplier;

        return {
          fiscalYear: project.fiscalYear ?? '',
          ciProjectId: project.projectId ?? '',
          projectStatus: project.projectStatus ?? '',
          unitCode: project.unitCode ?? '',
          partNumber: part.partNumber ?? '',
          supplierNumber: showCurrentSupplier ? currentSupplierNumber : '',
          supplierName:
            showCurrentSupplier && currentSupplierNumber
              ? (supplierNamesByNumbers.get(currentSupplierNumber) ?? '')
              : '',
          piecePrice: showCurrentSupplier
            ? toNumberOrNull(part.currentPiecePriceUsd)
            : null,
          newSupplierNumber: showNewSupplier ? newSupplierNumber : '',
          newSupplierName:
            showNewSupplier && newSupplierNumber
              ? (supplierNamesByNumbers.get(newSupplierNumber) ?? '')
              : '',
          newPiecePrice: showNewSupplier
            ? toNumberOrNull(part.newPiecePriceUsd)
            : null,
          forecast52Wk: toNumberOrNull(part.forecast52Week),
          extendedCost: toNumberOrNull(part.extendedCost)
        };
      });
    }
  );
  const wsCiProjects = buildWorksheet(ciProjectColumns, ciProjectRows);
  applyColumnNumberFormats(wsCiProjects, ciProjectColumns, ciProjectRows.length, {
    piecePrice: CURRENCY_NUM_FMT,
    newPiecePrice: CURRENCY_NUM_FMT,
    forecast52Wk: '#,##0;-#,##0;0',
    extendedCost: CURRENCY_NUM_FMT
  });
  XLSX.utils.book_append_sheet(wb, wsCiProjects, 'CI Projects');

  // Download via blob
  const url = window.URL.createObjectURL(
    new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filenameAddition} - Spend Shift Part Detail.xlsx`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, 0);
}
